import React, { FunctionComponent, MouseEvent, useEffect, useRef, useState } from 'react';
import { faEllipsisH, faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, createStyles, makeStyles, Theme, ClickAwayListener, Popper } from '@material-ui/core';
import clsx from 'clsx';
import { PropsWithChildren } from 'react';

const TOOLBAR_BUTTON_OPEN_EVENT = 'cuddles:toolbar-button-open';

let toolbarButtonInstanceCounter = 0;

export interface Props extends PropsWithChildren {
    disabled?: boolean;
    forceOpen?: boolean;
    id?: string;
    className?: string;
    zIndex?: number;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        containerDropDown: {
            display: 'flex',
            position: 'relative',
            width: '30px',
            justifyContent: 'center'
        },
        dropdown: {
            width: 'max-content',
            borderRadius: 14,
            padding: '10px 0px',
            backgroundColor: '#fff',
            border: 'solid 1px #000',
            display: 'flex',
            flexDirection: 'column'
        },
        icon: {
            cursor: 'pointer'
        }
    })
);

function checkIfCloseToBottom(element: HTMLElement | null, threshold = 100): boolean {
    if (!element) {
        return false;
    }

    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    return rect.bottom >= viewportHeight - threshold;
}

export const ToolbarButton: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const instanceIdRef = useRef(props.id ?? `toolbar-button-${++toolbarButtonInstanceCounter}`);
    const instanceId = instanceIdRef.current;

    const [show, setShow] = useState(false);
    const [isCloseToBottom, setIsCloseToBottom] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleOtherToolbarOpen = (event: Event) => {
            const openId = (event as CustomEvent<string>).detail;
            if (openId !== instanceId) {
                setShow(false);
            }
        };

        window.addEventListener(TOOLBAR_BUTTON_OPEN_EVENT, handleOtherToolbarOpen);
        return () => {
            window.removeEventListener(TOOLBAR_BUTTON_OPEN_EVENT, handleOtherToolbarOpen);
        };
    }, [instanceId]);

    useEffect(() => {
        const updatePosition = () => {
            setIsCloseToBottom(checkIfCloseToBottom(anchorRef.current));
        };

        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, []);

    const clickToolbarButtonHandler = (event: MouseEvent): void => {
        event.stopPropagation();
        if (props.disabled || props.forceOpen) {
            return;
        }

        setShow(prev => {
            const next = !prev;
            if (next) {
                setIsCloseToBottom(checkIfCloseToBottom(anchorRef.current));
                window.dispatchEvent(new CustomEvent(TOOLBAR_BUTTON_OPEN_EVENT, { detail: instanceId }));
            }
            return next;
        });
    };

    const handleClickAway = (event: React.MouseEvent | React.TouchEvent) => {
        if (props.forceOpen) {
            return;
        }

        const target = event.target as Node;
        if (dropdownRef.current?.contains(target) || anchorRef.current?.contains(target)) {
            return;
        }

        setShow(false);
    };

    useEffect(() => {
        if (props.forceOpen) {
            setShow(true);
        }
    }, [props.forceOpen]);

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div
                ref={anchorRef}
                className={clsx(classes.containerDropDown, props.className)}
                onClick={clickToolbarButtonHandler}
            >
                <Box
                    className={classes.icon}
                    id={props.id && props.id.concat('_toolbar_button')}
                >
                    {show ? (
                        <FontAwesomeIcon
                            color="#92B4A7"
                            icon={faEllipsisV}
                        />
                    ) : (
                        <FontAwesomeIcon
                            color="#000000"
                            icon={faEllipsisH}
                        />
                    )}
                </Box>

                <Popper
                    open={show}
                    anchorEl={anchorRef.current}
                    placement={isCloseToBottom ? 'top-end' : 'bottom-end'}
                    style={{ zIndex: props.zIndex ?? 1300 }}
                >
                    <Box
                        ref={dropdownRef}
                        className={classes.dropdown}
                        id={props.id && props.id.concat('_toolbar_list')}
                        onClick={(event: MouseEvent) => event.stopPropagation()}
                    >
                        {props.children}
                    </Box>
                </Popper>
            </div>
        </ClickAwayListener>
    );
};

export default ToolbarButton;
